import { supabase } from '../supabase';

export interface SubmissionContactId {
  id: string;
  contact_id: string | null;
}

export interface PollContactIdsOptions {
  maxAttempts?: number;
  delayMs?: number;
}

export type PollContactIdsResult =
  | { status: 'ready'; data: SubmissionContactId[] }
  | { status: 'timeout'; data: SubmissionContactId[] | null }
  | { status: 'error'; data: null; error: unknown };

const DEFAULTS = {
  maxAttempts: 10,
  delayMs: 300,
} as const;

/**
 * Polling : attend que le trigger Supabase ait rempli contact_id
 * sur toutes les form_submissions demandées.
 *
 * Retourne un discriminated union :
 * - 'ready'   : toutes les submissions ont un contact_id non-null
 * - 'timeout' : tentatives épuisées, certains contact_id restent null
 * - 'error'   : la requête Supabase a échoué (réseau, RLS, etc.)
 *
 * Le caller DOIT traiter les trois cas pour éviter de signaler un succès
 * alors que le paiement n'a pas pu être initialisé.
 */
export async function pollContactIds(
  submissionIds: string[],
  options?: PollContactIdsOptions
): Promise<PollContactIdsResult> {
  const maxAttempts = options?.maxAttempts ?? DEFAULTS.maxAttempts;
  const delayMs = options?.delayMs ?? DEFAULTS.delayMs;

  let lastData: SubmissionContactId[] | null = null;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));

    const { data, error } = await supabase
      .from('form_submissions')
      .select('id, contact_id')
      .in('id', submissionIds);

    if (error) {
      console.error('[BPI] erreur polling form_submissions', error);
      return { status: 'error', data: null, error };
    }

    lastData = data;

    if (data && data.length === submissionIds.length && data.every((s) => s.contact_id !== null)) {
      return { status: 'ready', data };
    }
  }

  console.error(
    `[BPI] contact_id toujours null après ${maxAttempts * delayMs}ms de polling`,
    lastData
  );
  return { status: 'timeout', data: lastData };
}
